import {
  DEFAULT_CANDIDATE_STATUSES,
  DEFAULT_STATUS_REQUIRE_REASON_CHANGE,
  DEFAULT_DASHBOARD_TABLE_FIELDS_CANDIDATE,
  DEFAULT_REQUEST_TIME_OFF_CATEGORIES,
  DEFAULT_CANDIDATE_AVAILABLE_JOBS_TABLE_FIELDS,
  DEFAULT_ANALYTICS_DASHBOARD_SELECTION
} from "../../constants/default-values";
import axios from 'axios';
import { getPreviousDate, dateInDjangoFormat, addDaysToDate } from "../../utils/datetime-handling";
import { getLatestList } from "../../utils/data-handling";
import tryGet from '../../utils/try-get'
import { getRequestUrlParams } from './../../utils/data-handling';
import { appendBackgroundParam } from "../../utils/query-handling";
import { mergeArrayOfObject } from "../../utils/array-handling";

const state = {
  candidateStatuses: DEFAULT_CANDIDATE_STATUSES,
  candidateAdminChecklist: [],
  experienceOptions: ["2-5 years", "5-10 years", "10 plus years"],
  commitmentOptions: [
    "Long Term (longer than 1 year)",
    "Short Term (1 year or less)",
    "Temporary"
  ],
  availabilityOptions: ["Full Time", "Part Time", "Come and Go", "Live In"],
  drvingStatuses: ["D.L. and Car", "Driver's License only", "Neither"],
  certificationStatuses: ["Yes", "Willing to get certified", "No"],
  vaccinationStatuses: ["Yes", "Willing to get", "No"],
  petsOptions: ["Dog", "Cat", "Neither"],
  travelOptions: ["Domestic", "International", "No travel"],
  activeStatus: "Profile Complete",
  candidateList: [],
  candidateListMainInfo: null,
  candidateListIdNames: null,
  candidateLastLoginFilter: null,
  availabilities: null, // List all availabilities of all candidates
  weekAvailabilities: null, // List all week availabilities of all candidates
  candidateInfoSelectionFields: null, // List information of candidates to show in the available candidates
  publicCandidateList: null,
  candidateListDateInfo: null, // List of start date, end date information of candidates
  selectedCandidate: null,
};

const mutations = {
  setCandidateStatuses(state, candidateStatuses) {
    state.candidateStatuses = candidateStatuses;
  },
  setCandidateAdminChecklist(state, candidateAdminChecklist) {
    state.candidateAdminChecklist = candidateAdminChecklist;
  },
  setCandidateList(state, candidateList) {
    state.candidateList = candidateList;
  },
  setCandidateListMainInfo(state, candidateListMainInfo) {
    state.candidateListMainInfo = candidateListMainInfo;
  },
  setCandidateListIdNames(state, candidateListIdNames) {
    state.candidateListIdNames = candidateListIdNames;
  },
  setPublicCandidateList(state, payload) {
    state.publicCandidateList = payload;
  },
  setCandidateLastLoginFilter(state, payload) {
    state.candidateLastLoginFilter = payload;
  },
  setAvailabilities(state, payload) {
    state.availabilities = payload;
  },
  setWeekAvailabilities(state, payload) {
    state.weekAvailabilities = payload;
  },
  setCandidateInfoSelectionFields(state, payload) {
    state.candidateInfoSelectionFields = payload;
  },
  setCandidateListDateInfo(state, payload) {
    state.candidateListDateInfo = payload;
  },
  setSelectedCandidate(state, payload) {
    state.selectedCandidate = payload;
  },
};

const actions = {
  setCandidateStatuses({ commit }, payload) {
    if (payload && Array.isArray(payload) && payload.length > 0) {
      commit("setCandidateStatuses", payload);
    } else {
      // If payload is invalid (not an array or empty array), default candidate statuses are used
      commit("setCandidateStatuses", DEFAULT_CANDIDATE_STATUSES);
    }
  },
  setCandidateAdminChecklist({ commit }, payload) {
    if (payload && Array.isArray(payload)) {
      commit("setCandidateAdminChecklist", payload);
    } else {
      // If payload is invalid (not an array), checklist is empty array
      commit("setCandidateAdminChecklist", []);
    }
  },

  setCandidateList({ commit }, payload) {
    commit("setCandidateList", payload);
  },
  setCandidateListMainInfo({ commit }, payload) {
    commit("setCandidateListMainInfo", payload);
  },
  setPublicCandidateList({ commit }, payload) {
    commit("setPublicCandidateList", payload);
  },
  setAvailabilities({ commit }, payload) {
    commit("setAvailabilities", payload);
  },
  setWeekAvailabilities({ commit }, payload) {
    commit("setWeekAvailabilities", payload);
  },
  setCandidateInfoSelectionFields({ commit }, payload) {
    commit("setCandidateInfoSelectionFields", payload);
  },
  setCandidateLastLoginFilter({ commit }, payload) {
    commit("setCandidateLastLoginFilter", payload);
  },
  setCandidateLastLoginRetrievalDays({ commit }, numberOfDay) {
    if (!numberOfDay || isNaN(numberOfDay)) {
      commit("setCandidateLastLoginFilter", null);
      return;
    }
    const date = new Date();
    date.setDate(date.getDate() - numberOfDay);
    const dateString = date.getFullYear().toString() +
      "-" +
      ("0" + (date.getMonth() + 1).toString()).slice(-2) +
      "-" +
      ("0" + date.getDate().toString()).slice(-2);
    commit("setCandidateLastLoginFilter", dateString);
  },
  reloadCandidateList({ commit, state, rootState, getters }, params) {
    const loadAllFields = params && params.loadAllFields;
    const loadExportFields = params && params.loadExportFields;
    const inBackground = params && params.inBackground;
    return new Promise(async (resolve) => {
      let url = `/get-users/?user_type=candidates&agency=${rootState.authentication.agencyID}`;
      if (inBackground) {
        url = appendBackgroundParam(url);
        let lastUpdated = null;
        if (getters.candidateList.length) {
          const lastItem = getters.candidateList.reduce((max, user) => max.last_updated_date > user.last_updated_date ? max : user);
          if (lastItem) {
            lastUpdated = lastItem.last_updated_date;
          }
        }
        if (lastUpdated) {
          url = `${url}&last_updated=${lastUpdated}`;
        }
      }
      if (!loadAllFields) {
        let listModels = [...getters.candidateTableFields, ...getters.candidateSearchFields].map(field => field.model);
        listModels = listModels.filter(model => model !== 'first_name_last_name' && model !== 'secondary_first_name_secondary_last_name'
          && model !== 'adminNotes');
        listModels = [...listModels, 'created_date', 'id', 'status', 'types', 'first_name', 'last_name',
          'locations', 'last_login', 'last_updated_date', 'last_activity', 'email', 'phone', 'address1', 'is_candidate', 'marketing_source',
          'secondary_first_name', 'secondary_last_name', 'tags', 'user_latitude', 'user_longitude', 'country'];
        listModels = [...new Set(listModels)]

        if (loadExportFields) {
          if (
            rootState.agency.advance_setting &&
            rootState.agency.advance_setting.candidate_mailing_fields
          ) {
            let setFields =
              rootState.agency.advance_setting.candidate_mailing_fields;
            let exportFields = [...setFields].map(field => field.model)
            listModels = [...listModels, ...exportFields]
          }
        }
        // Add filter param (last login date)
        url += `&fields=${listModels.join(',')}`;
      }
      if (state.candidateLastLoginFilter) {
        url += `&recent_activity_filter=${state.candidateLastLoginFilter}`;
      }
      const res = await axios.get(url);
      let listUsers = res.data.users;
      if (inBackground) {
        listUsers = mergeArrayOfObject(getters.candidateList, listUsers);
      }
      commit("setCandidateList", listUsers);
      resolve();
      return;
    })
  },
  async loadCandidateListByModels({ rootState }, listModels) {
    return new Promise(async (resolve) => {
      const url = `/get-users/?user_type=candidates&agency=${rootState.authentication.agencyID}&fields=${listModels.join(',')}`;
      const res = await axios.get(url);
      resolve(res.data.users);
    });
  },
  loadAvailabilities({ commit, state, rootState }) {
    return new Promise(async (resolve) => {
      if (state.availabilities) {
        resolve();
        return;
      }
      // Only load availability start from last month
      const startDate = dateInDjangoFormat(addDaysToDate(-30));
      const res = await axios.get(
        `/api/availabilities/?agency=${rootState.authentication.agencyID}&start_date=${startDate}`
      );
      commit("setAvailabilities", res.data);
      resolve();
    });
  },
  loadWeekAvailabilities({ commit, state, rootState }) {
    return new Promise(async (resolve) => {
      if (state.weekAvailabilities) {
        resolve();
        return;
      }
      const res = await axios.get(
        "/api/weekavailabilities/?agency=" +
        rootState.authentication.agencyID.toString()
      );
      commit("setWeekAvailabilities", res.data);
      resolve();
    });
  },
  loadCandidateListMainInfo({ commit, state, rootState }, params) {
    return new Promise(async (resolve) => {
      let isReload = false;
      let isBackground = false;
      if (params) {
        isReload = params.isReload;
        isBackground = params.isBackground;
      }
      if (!isReload && state.candidateListMainInfo) {
        resolve();
        return;
      }
      let url = `/get-users/?user_type=candidates&names_and_emails_only=true&agency=${rootState.authentication.agencyID}`;
      if (isBackground) {
        url = `${url}&in_background=true`
      }
      const res = await axios.get(
        url
      );
      if (res && res.data) {
        commit("setCandidateListMainInfo", res.data.users);
      }
      resolve();
    });
  },
  loadCandidateListIdNames({ commit, state, rootState }, params) {
    return new Promise(async (resolve) => {
      let isReload = false;
      let isBackground = false;
      if (params) {
        isReload = params.isReload;
        isBackground = params.isBackground;
      }
      if (!isReload && state.candidateListIdNames) {
        resolve();
        return;
      }
      let url = `/get-users/?agency=${rootState.authentication.agencyID.toString()}&user_type=candidates&fields=id,first_name,last_name`;
      if (isBackground) {
        url = `${url}&in_background=true`
      }
      const res = await axios.get(
        url
      );
      if (res && res.data) {
        commit("setCandidateListIdNames", res.data.users);
      }
      resolve();
    });
  },
  loadCandidateInfoSelectionFields({ commit, state, getters, rootState }) {
    return new Promise(async (resolve) => {
      if (state.candidateInfoSelectionFields) {
        resolve();
        return;
      }
      if (
        !getters.shiftJobSelectionProfileFields ||
        !getters.shiftJobSelectionProfileFields.length
      ) {
        resolve();
        return;
      }
      const usersRes = await axios.get(
        `/get-public-candidates-list/?agency=${rootState.authentication.agencyID.toString()}`
      );
      if (usersRes && usersRes.data) {
        commit("setCandidateInfoSelectionFields", usersRes.data.users);
      }
      resolve();
    });
  },
  loadCandidateListDateInfo({ commit, state, getters, rootState }, params) {
    return new Promise(async (resolve) => {
      let isReload = false;
      let isBackground = false;
      if (params) {
        isReload = params.isReload;
        isBackground = params.isBackground;
      }
      if (!isReload && state.candidateListDateInfo) {
        resolve();
        return;
      }
      if (getters.candidateStartDateModel || getters.candidateEndDateModel) {
        let fieldModels =
          getters.candidateStartDateModel || getters.candidateEndDateModel;
        if (getters.candidateStartDateModel && getters.candidateEndDateModel) {
          fieldModels =
            getters.candidateStartDateModel + "," + getters.candidateEndDateModel;
        }
        let url = `/get-users/?agency=${rootState.authentication.agencyID.toString()}&fields=id,${fieldModels}`;
        if (isBackground) {
          url = `${url}&in_background=true`
        }
        const usersRes = await axios.get(
          url
        );
        commit("setCandidateListDateInfo", usersRes.data.users);
      }
      resolve();
    });
  },
  async reloadCandidateListMainInfo({ commit }) {
    const res = await axios.get(
      `/get-users/?user_type=candidates&names_and_emails_only=true`
    );
    if (res) {
      commit('setCandidateListMainInfo', res.data.users);
    }
  },
  async getCandidateList(_, payload) {
    return await new Promise((resolve, reject) => {
      let url = 'optimize/get-candidates/?'
      if (payload) {
        url = `${url}&${getRequestUrlParams(payload)}`
      }
      axios.get(url)
        .then((response) => {
          if (response.status === 200) {
            resolve(response);
          }
        }).catch((err) => {
          reject(err)
        });
    })
  },
  setSelectedCandidate({ commit }, payload) {
    commit("setSelectedCandidate", payload);
  },
};

const getters = {
  candidateListIncludeDateInfo(state) {
    const candidateListMainInfo = state.candidateListMainInfo;
    const candidateListDateInfo = state.candidateListDateInfo;
    if (!candidateListMainInfo || !candidateListDateInfo) {
      return candidateListMainInfo || candidateListDateInfo
    }

    const mapUserInfo = {};
    for (const user of candidateListDateInfo) {
      mapUserInfo[user.id] = user;
    }
    const result = [];
    for (const candidate of candidateListMainInfo) {
      if (mapUserInfo[candidate.id]) {
        const dateInfo = mapUserInfo[candidate.id];
        result.push({ ...candidate, ...dateInfo })
      }
    }
    return result;
  },
  candidateStatuses(state, getters, rootState) {
    let result = tryGet(() => rootState.agency.agencyPublic.candidate_statuses);
    if (result && result.length) {
      return result;
    }
    return DEFAULT_CANDIDATE_STATUSES;
  },
  candidateAdminChecklist(state) {
    return state.candidateAdminChecklist;
  },
  experienceOptions(state) {
    return state.experienceOptions;
  },
  commitmentOptions(state) {
    return state.commitmentOptions;
  },
  availabilityOptions(state) {
    return state.availabilityOptions;
  },
  drvingStatuses(state) {
    return state.drvingStatuses;
  },
  certificationStatuses(state) {
    return state.certificationStatuses;
  },
  vaccinationStatuses(state) {
    return state.vaccinationStatuses;
  },
  petsOptions(state) {
    return state.petsOptions;
  },
  travelOptions(state) {
    return state.travelOptions;
  },
  activeStatus(state) {
    return state.activeStatus;
  },
  candidateList(state) {
    return state.candidateList || [];
  },
  candidateListMainInfo(state) {
    return state.candidateListMainInfo;
  },
  candidateListIdNames(state) {
    return state.candidateListIdNames;
  },
  publicCandidateList(state) {
    return state.publicCandidateList;
  },
  candidateLastLoginFilter(state) {
    return state.candidateLastLoginFilter;
  },
  candidateTypes(state, getters, rootState) {
    return tryGet(() => rootState.agency.agencyPublic.candidate_types) || [];
  },
  candidateTags(state, getters, rootState) {
    return tryGet(() => rootState.agency.agencyPublic.candidate_tags) || [];
  },
  availabilities(state) {
    return state.availabilities;
  },
  weekAvailabilities(state) {
    return state.weekAvailabilities;
  },
  shiftJobSelectionProfileFields(state, getters, rootState) {
    const agency = rootState.agency.agencyPublic;
    if (
      agency &&
      agency.public_advance_setting &&
      agency.public_advance_setting.shift_selection_profile_fields) {
      return agency.public_advance_setting.shift_selection_profile_fields;
    }
    return [];
  },
  shiftViewFullProfile(state, getters, rootState) {
    const agency = rootState.agency.agencyPublic;
    if (
      agency &&
      agency.public_advance_setting &&
      agency.public_advance_setting.shift_view_full_profile) {
      return agency.public_advance_setting.shift_view_full_profile;
    }
    return false;
  },
  candidateInfoSelectionFields(state) {
    return state.candidateInfoSelectionFields;
  },
  defaultCandidateBufferTime(state, getters, rootState) {
    const agency = rootState.authentication.agency;
    if (
      agency &&
      agency.advance_setting &&
      agency.advance_setting.default_candidate_buffer_time != null) {
      return agency.advance_setting.default_candidate_buffer_time;
    }
    return 1;
  },
  hideMyJobsCandidate(state, getters, rootState) {
    const agency = rootState.authentication.agency;
    if (
      agency &&
      agency.advance_setting &&
      agency.advance_setting.hide_my_jobs_candidate != null) {
      return agency.advance_setting.hide_my_jobs_candidate;
    }
    return false;
  },
  candidateCanEditBufferTime(state, getters, rootState) {
    const agency = rootState.authentication.agency;
    if (
      agency &&
      agency.advance_setting &&
      agency.advance_setting.candidate_can_edit_buffer_time != null) {
      return agency.advance_setting.candidate_can_edit_buffer_time;
    }
    return false;
  },
  candidatesCanSetRate(state, getters, rootState) {
    const agency = rootState.agency.agencyPublic;
    if (
      agency &&
      agency.public_advance_setting &&
      agency.public_advance_setting.candidates_can_set_rate != null) {
      return agency.public_advance_setting.candidates_can_set_rate;
    }
    return false;
  },
  adminCanSetCandidateRate(state, getters, rootState) {
    const agency = rootState.agency.agencyPublic;
    if (
      agency &&
      agency.public_advance_setting &&
      agency.public_advance_setting.admin_can_set_candidate_rate != null) {
      return agency.public_advance_setting.admin_can_set_candidate_rate;
    }
    return false;
  },
  hideCandidateShareableLink(state, getters, rootState) {
    const agency = rootState.agency.agencyPublic;
    if (
      agency &&
      agency.public_advance_setting &&
      agency.public_advance_setting.hide_candidate_shareable_link != null) {
      return agency.public_advance_setting.hide_candidate_shareable_link;
    }
    return false;
  },
  candidateTypesSetSchedule(state, getters, rootState) {
    const agency = rootState.authentication.agency;
    if (
      agency &&
      agency.advance_setting &&
      agency.advance_setting.set_schedule_types != null) {
      return agency.advance_setting.set_schedule_types;
    }
    return [];
  },
  candidateStatusesSetSchedule(state, getters, rootState) {
    const agency = rootState.authentication.agency;
    if (
      agency &&
      agency.set_schedule_statuses != null) {
      return agency.set_schedule_statuses;
    }
    return [];
  },
  candidateCanSetSchedule(state, getters, rootState) {
    const agency = rootState.authentication.agency;
    if (
      agency &&
      agency.candidates_set_schedules != null) {
      return agency.candidates_set_schedules;
    }
    return false;
  },
  candidateStartDateModel(state, getters, rootState) {
    const agency = rootState.authentication.agency;
    if (
      agency &&
      agency.advance_setting &&
      agency.advance_setting.candidate_start_date_model != null) {
      return agency.advance_setting.candidate_start_date_model;
    }
    return null;
  },
  candidateEndDateModel(state, getters, rootState) {
    const agency = rootState.authentication.agency;
    if (
      agency &&
      agency.advance_setting &&
      agency.advance_setting.candidate_end_date_model != null) {
      return agency.advance_setting.candidate_end_date_model;
    }
    return null;
  },
  numberDaysCannotRequestTimeOff(state, getters, rootState) {
    const agency = rootState.authentication.agency;
    if (
      agency &&
      agency.advance_setting &&
      agency.advance_setting.number_days_cannot_request_time_off != null) {
      return agency.advance_setting.number_days_cannot_request_time_off;
    }
    return 0;
  },
  defaultViewAvailableJobsCalendar(state, getters, rootState) {
    const agency = rootState.authentication.agency;
    if (
      agency &&
      agency.advance_setting &&
      agency.advance_setting.default_view_available_jobs_calendar != null) {
      return agency.advance_setting.default_view_available_jobs_calendar;
    }
    return 'Week';
  },
  candidateStatusRequireReason(state, getters, rootState) {
    const agency = rootState.agency.agencyPublic;
    if (
      agency &&
      agency.public_advance_setting &&
      agency.public_advance_setting.candidate_status_require_reason != null) {
      return agency.public_advance_setting.candidate_status_require_reason;
    }
    return DEFAULT_STATUS_REQUIRE_REASON_CHANGE;
  },
  minChildrenAgeField(state, getters, rootState) {
    const agency = rootState.agency.agencyPublic;
    if (
      agency &&
      agency.public_advance_setting &&
      agency.public_advance_setting.min_children_age_field) {
      return agency.public_advance_setting.min_children_age_field;
    }
    return null;
  },
  candidateStatusReasonOptions(state, getters, rootState) {
    const agency = rootState.agency.agencyPublic;
    if (
      agency &&
      agency.public_advance_setting &&
      agency.public_advance_setting.candidate_status_reason_options) {
      return agency.public_advance_setting.candidate_status_reason_options;
    }
    return {};
  },
  candidateSecondStatuses(state, getters, rootState) {
    const agency = rootState.agency.agencyPublic;
    if (
      agency &&
      agency.public_advance_setting &&
      agency.public_advance_setting.candidate_second_statuses) {
      return agency.public_advance_setting.candidate_second_statuses;
    }
    return [];
  },
  candidateTableFields(state, getters, rootState) {
    // Check admin level setting
    const adminSetting = rootState.general.adminSetting;
    if (adminSetting && adminSetting.setting && adminSetting.setting.use_admin_level__candidate_table_fields) {
      const adminLevelSetting = adminSetting.setting.admin_level__candidate_table_fields;
      if (adminLevelSetting && adminLevelSetting.length) {
        return adminLevelSetting;
      }
    }
    const agency = rootState.authentication.agency;
    const listFields = agency.candidate_table_fields;
    if (listFields && listFields.length) {
      return listFields;
    }
    return DEFAULT_DASHBOARD_TABLE_FIELDS_CANDIDATE;
  },
  candidateSearchFields(state, getters, rootState) {
    const agency = rootState.authentication.agency;
    if (agency && agency.advance_setting) {
      const listFields = agency.advance_setting.candidate_search_fields;
      if (listFields && listFields.length) {
        return listFields;
      }
    }
    return [];
  },
  candidateAnalyticsDashboardFields(state, getters, rootState) {
    const agency = rootState.authentication.agency;
    if (agency && agency.advance_setting) {
      const listFields = agency.advance_setting.candidate_analytics_dashboard_fields;
      if (listFields && listFields.length) {
        return listFields;
      }
    }
    return DEFAULT_ANALYTICS_DASHBOARD_SELECTION;
  },
  candidateStatisticsStatuses(state, getters, rootState) {
    const agency = rootState.authentication.agency;
    if (agency && agency.public_advance_setting.candidate_statistic_status) {
      return agency.public_advance_setting.candidate_statistic_status
    }
    return DEFAULT_CANDIDATE_STATUSES
  },
  selectedCandidate(state) {
    return state.selectedCandidate;
  },
  candidateTableStatusColors: (state, getters, rootState) => tryGet(() => rootState.agency.agencyPublic.candidate_table_status_color) || [],
  labelCandidateStatuses: (state, getters, rootState) => tryGet(() => rootState.agency.agencyPublic.public_advance_setting.label_candidate_statuses),
  labelCandidateSecondStatuses: (state, getters, rootState) => tryGet(() => rootState.agency.agencyPublic.public_advance_setting.label_candidate_second_statuses),
  candidateDocumentDefaultScope: (state, getters, rootState) => tryGet(() => rootState.agency.agencyPublic.public_advance_setting.candidate_default_document_scope) || 'private',
  hideMyScheduleCandidate: (state, getters, rootState) => tryGet(() => rootState.authentication.agency.advance_setting.hide_my_schedule_candidate) || false,
  filterTypesThatCanSetSchedule: (state, getters, rootState) => tryGet(() => rootState.authentication.agency.advance_setting.filter_types_that_can_set_schedule) || [],
  hideMyFamiliesCandidate: (state, getters, rootState) => tryGet(() => rootState.authentication.agency.advance_setting.hide_my_families_candidate) || false,
  candidateCanUploadJobFile: (state, getters, rootState) => tryGet(() => rootState.authentication.agency.advance_setting.candidate_can_upload_job_file) || false,
  hideCandidateDashboardMenuBar: (state, getters, rootState) => tryGet(() => rootState.authentication.agency.advance_setting.hide_candidate_dashboard_menu_bar) || false,
  candidateProfileTopWelcomeText: (state, getters, rootState) => tryGet(() => rootState.authentication.agency.advance_setting.candidate_profile_top_welcome_text) || '',
  candidateCanRequestTimeOff: (state, getters, rootState) => tryGet(() => rootState.authentication.agency.advance_setting.candidate_can_request_timeoff) || false,
  candidateProfileCompleteButtonText: (state, getters, rootState) => tryGet(() => rootState.authentication.agency.advance_setting.candidate_profile_complete_button_text) || 'My Profile is Complete, Notify Agency',
  secondaryCandidateProfileCompleteButtonText: (state, getters, rootState) => tryGet(() => rootState.authentication.agency.advance_setting.secondary_candidate_profile_complete_button_text) || 'My Profile is Complete, Notify Agency',
  doNotAllowCandidatesDeleteUploadedDocuments: (state, getters, rootState) => tryGet(() => rootState.authentication.agency.advance_setting.do_not_allow_candidates_delete_uploaded_documents) || false,
  wordUsedForMyProfile: (state, getters, rootState) => tryGet(() => rootState.authentication.agency.public_advance_setting.word_used_for_my_profile) || "My Profile",
  wordUsedForAvailableJobs: (state, getters, rootState) => tryGet(() => rootState.authentication.agency.public_advance_setting.word_used_for_available_jobs) || "Available Jobs ",
  wordUsedForMySchedule: (state, getters, rootState) => tryGet(() => rootState.authentication.agency.public_advance_setting.word_used_for_my_schedule) || "My Schedule",
  wordUsedForMyJobs: (state, getters, rootState) => tryGet(() => rootState.authentication.agency.public_advance_setting.word_used_for_my_jobs) || "My Jobs",
  wordUsedForMyFamilies: (state, getters, rootState) => tryGet(() => rootState.authentication.agency.public_advance_setting.word_used_for_my_families) || "My Families",
  thirdCandidateProfileCompleteButtonText: (state, getters, rootState) => tryGet(() => rootState.authentication.agency.advance_setting.third_candidate_profile_complete_button_text) || 'My Profile is Complete, Notify Agency',
  timeOffCategories: (state, getters, rootState) => tryGet(() => rootState.authentication.agency.public_advance_setting.time_off_categories) || DEFAULT_REQUEST_TIME_OFF_CATEGORIES,
  candidateDefaultTableView: (state, getters, rootState) => tryGet(() => rootState.authentication.agency.public_advance_setting.candidate_default_table_view) || false,
  candidateAvailableJobsTableFields: (state, getters, rootState) => tryGet(() => rootState.authentication.agency.advance_setting.candidate_available_jobs_table_columns) || DEFAULT_CANDIDATE_AVAILABLE_JOBS_TABLE_FIELDS,
  notifyEmailsCandidateRequestTimeOff: (state, getters, rootState) => tryGet(() => rootState.authentication.agency.advance_setting.notify_emails_candidate_request_time_off) || [],
  notifyEmailsCandidateChangeRecurringAvailability: (state, getters, rootState) => tryGet(() => rootState.authentication.agency.advance_setting.notify_emails_candidate_change_recurring_availability) || [],
  candidateTypesCanNotEditRecurringAvailability: (state, getters, rootState) => tryGet(() => rootState.authentication.agency.advance_setting.candidate_types_cannot_edit_recurring_availability) || [],
  allowCandidateToEditRecurringAvailability: (state, getters, rootState) => tryGet(() => rootState.authentication.agency.advance_setting.allow_candidate_to_edit_recurring_availability) || false,
  preventCandidatesInputtingDailyAvailability: (state, getters, rootState) => tryGet(() => rootState.authentication.agency.advance_setting.prevent_candidates_inputting_daily_availability) || false,
  hideViewPreviousJobApplications: (state, getters, rootState) => tryGet(() => rootState.authentication.agency.advance_setting.hide_view_previous_job_applications) || false,
  candidatesCanAssignBadge: (state, getters, rootState) => tryGet(() => rootState.authentication.agency.advance_setting.candidate_can_assign_badge) || false,
  useTimeoffCredit: (state, getters, rootState) => tryGet(() => rootState.authentication.agency.advance_setting.use_timeoff_credit) || false,
  updateCandidateStatusWhenAllDocumentSigned: (state, getters, rootState) => tryGet(() => rootState.authentication.agency.advance_setting.update_candidate_status_when_all_document_signed) || false,
  candidateStatusToUpdateWhenAllDocumentSigned: (state, getters, rootState) => tryGet(() => rootState.authentication.agency.advance_setting.candidate_status_to_update_when_all_documents_signed) || '',
  candidateCannotModifyExpireDate: (state, getters, rootState) => tryGet(() => rootState.authentication.agency.advance_setting.candidate_cannot_modify_expire_date) || false,
  removeCandidateUploadAdditionalDocumentButton: (state, getters, rootState) => tryGet(() => rootState.authentication.agency.advance_setting.remove_candidate_upload_additional_document_button) || false,
  showCandidatePdfButtonOfClientProfile: (state, getters, rootState) => tryGet(() => rootState.authentication.agency.public_advance_setting.show_candidate_pdf_button_of_client_profile) || false,
  hideBufferTimeCandidateProfile: (state, getters, rootState) => tryGet(() => rootState.authentication.agency.advance_setting.hide_buffer_time_from_candidate_profile) || false,
  hideMaximumBookingsUserCanHaveCandidateProfile: (state, getters, rootState) => tryGet(() => rootState.authentication.agency.advance_setting.hide_maximum_bookings_user_can_have_from_candidate_profile) || false,
  hideOptionCandidateEditProfileHeader: (state, getters, rootState) => tryGet(() => rootState.authentication.agency.advance_setting.hide_option_candidate_edit_profile_header) || false,
  showCandidateDialogInRight: (state, getters, rootState) => tryGet(() => rootState.authentication.agency.advance_setting.show_candidate_dialog_in_right) || false,
  candidateReapplicationStatuses: (state, getters, rootState) => tryGet(() => rootState.agency.agencyPublic.candidate_reapplication_statuses) || [],
  notifyAgencyWhenCandidateUploadAnyDocument: (state, getters, rootState) => tryGet(() => rootState.authentication.agency.advance_setting.agency_gets_notified_when_candidate_upload_any_document) || false,
  interviewNote1: (state, getters, rootState) => tryGet(() => rootState.agency.public_advance_setting.interview_heading_candidate_1) || "Phone Screen Note",
  interviewNote2: (state, getters, rootState) => tryGet(() => rootState.agency.public_advance_setting.interview_heading_candidate_2) || "Interview Note",
  candidateSearchBarOpenByDefault: (state, getters, rootState) => tryGet(() => rootState.agency.agencyPublic.advance_setting.candidate_search_bar_open_by_default) || false,
};

export default {
  state,
  mutations,
  actions,
  getters
};



// WEBPACK FOOTER //
// ./src/store/modules/candidates.js